import styles from './client-activity.module.scss';
import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TableContainer,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getCountry } from 'utils/country';
import { setDateFormat } from 'utils/date';
import { setActivityLogInfo } from 'utils/externalAccess';
import { ReactComponent as ArrowDown } from 'icons/chevron-down.svg';
import { ReactComponent as DownloadIcon } from 'icons/download.svg';
import { get as getAssessment } from 'api/needsassessments';
import { downloadPdfReport } from 'utils/pdfReport';
import { getWorkplaceStyleName } from 'services/inSite/workplaceStyles';
import CircularProgress from '@mui/material/CircularProgress';
import { tableHeaders, userTypes } from 'constants/externalAccess';
import { weakKey } from 'utils/objects';
import paths from 'constants/paths';
import { openFullPathInNewTab } from 'utils/paths';

export default function ClientActivity(props) {
  const { clientActivityList, clientNaId, brokerNaId } = props;
  const { t } = useTranslation();

  const [activities, setActivities] = useState(clientActivityList);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);

  useEffect(() => {
    setActivities(clientActivityList);
  }, [clientActivityList]);

  const onSortHandler = (sortField) => {
    const asc = orderBy === sortField && order === 'asc';
    setOrder(asc ? 'desc' : 'asc');
    setOrderBy(sortField);
    const items = activities.sort((a, b) => {
      const sortAsc = a[sortField] > b[sortField] ? 1 : -1;
      const sortDesc = a[sortField] < b[sortField] ? 1 : -1;
      return asc ? sortAsc : sortDesc;
    });
    setActivities(items);
  };

  const downloadPdf = async (type, idx) => {
    setSelectedAssessment(idx);
    setIsDownloading(true);
    const currentAssessment = await getAssessment(
      type === userTypes.readOnly ? brokerNaId : clientNaId,
    );
    await downloadPdfReport({
      id: currentAssessment.id,
      name: currentAssessment.name,
      createdAt: currentAssessment.createdAt,
      workplace: t(getWorkplaceStyleName(currentAssessment.workplaceStyleSelected)),
      terms: t('report.termsOfAgreement'),
    });
    setIsDownloading(false);
    setSelectedAssessment('');
  };

  const openAssessment = async (activityLog) => {
    setActivityLogInfo(activityLog);
    const naId =
      activityLog.type === userTypes.readOnly
        ? externalAccessConfig.brokerNaId
        : externalAccessConfig.clientNaId;
    openFullPathInNewTab(`${paths.step}${paths.steps.currentSituation}/${naId}`);
  };

  return (
    <div className={styles.clinet_activity__container}>
      <h2 className={styles.clinet_activity__title}>{t('clientConfig.clientActivity')}</h2>
      <p className={styles.clinet_activity__description}>
        {t('clientConfig.clientActivityDescription')}
      </p>
      <div className={styles.cc__table_container}>
        {activities.length ? (
          <TableContainer data-testid="ClientActivityTable" sx={{ maxHeight: 334, border: 'none' }}>
            <Table stickyHeader className={styles.cc__table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {Object.values(tableHeaders).map((header) => {
                    return (
                      <TableCell key={header.title}>
                        <TableSortLabel
                          data-testid={`SortLabel-${header.title}`}
                          IconComponent={ArrowDown}
                          active={orderBy === header.title}
                          direction={orderBy === header.title ? order : 'desc'}
                          onClick={() => onSortHandler(header.title)}>
                          {t(`clientConfig.clientActivityTable.${header.title}`)}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity, idx) => (
                  <TableRow
                    key={weakKey(activity)}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    data-testid={`ActivityLogRow-${idx}`}
                    onClick={() => openAssessment(activity)}>
                    <TableCell>
                      <div className={styles.cc__table_content}>{activity.clientEmail}</div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.cc__table_content}>{activity.type}</div>
                    </TableCell>
                    <TableCell>
                      {activity.type === 'Client' ? (
                        <div className={styles.cc__table_content}>
                          {setDateFormat(activity.createdAt, getCountry())}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className={styles.cc__table_content}>
                        {activity.type === 'Client' ? (
                          <div className={styles.cc__table_content}>
                            {activity.updatedAt
                              ? setDateFormat(activity.updatedAt, getCountry())
                              : ''}
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={styles.cc__table_content}>
                        {setDateFormat(activity.accessedAt, getCountry())}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className={styles.cc__table_actions}>
                        <Button
                          data-testid={`DownloadButton-${idx}`}
                          disabled={isDownloading}
                          onClick={() => downloadPdf(activity.type, idx)}
                          className={styles.cc__table_btn}>
                          {isDownloading && selectedAssessment === idx ? (
                            <CircularProgress color={'primary'} size={16} />
                          ) : (
                            <DownloadIcon />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div data-testid="ClientActivityNoContent" className={styles.cc__no_content}>
            <span>{t('clientConfig.noClientActivity')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
